"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useSiteData } from "../lib/site-context";
import { useLangContext } from "../lib/lang-context";
import { displayRef } from "../lib/mbm-helpers";
import { mapToPtPath } from "../lib/route-map";
import { withLocale } from "../lib/i18n";

const SUGGEST_MIN_CHARS = 3;
const SUGGEST_DEBOUNCE_MS = 250;

function snippetFromDesc(desc) {
  const flat = String(desc ?? "").replace(/\s+/g, " ").trim();
  if (flat.length <= 110) return flat;
  return `${flat.slice(0, 110).trimEnd()}…`;
}

// Mesma regra usada no CatalogPage (mbm-pages.jsx): slug "<nome>-<id>",
// com id no fim pra que parseProductIdFromSlug consiga abrir o detalhe
// mesmo que o nome mude no admin.
function suggestProductSlug(product) {
  const id = product?.id;
  if (id == null) return null;
  const slug = String(product?.name?.pt ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug ? `${slug}-${id}` : String(id);
}

function SearchIcon({ size = 18 }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Ícones inline pra utility bar do Topbar. Mantidos junto ao SearchIcon
// pra centralizar o tema visual (stroke=currentColor, 1.7 weight).
function PhoneIcon({ size = 14 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.28-1.28a2 2 0 0 1 2.11-.45c.9.34 1.84.57 2.8.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function MailIcon({ size = 14 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function InstagramIcon({ size = 16 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function FacebookIcon({ size = 16 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="currentColor" focusable="false">
      <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.25-1.5 1.6-1.5h1.6V4.5c-.3 0-1.25-.1-2.35-.1-2.32 0-3.9 1.4-3.9 4v2.4H7.7V14h2.75v8h3.05Z" />
    </svg>
  );
}

function YouTubeIcon({ size = 16 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="currentColor" focusable="false">
      <path d="M23.5 7.3c-.27-1-1.06-1.8-2.06-2.07C19.62 4.75 12 4.75 12 4.75s-7.62 0-9.44.48c-1 .27-1.79 1.06-2.06 2.07C0 9.12 0 12 0 12s0 2.88.5 4.7c.27 1 1.06 1.8 2.06 2.07 1.82.48 9.44.48 9.44.48s7.62 0 9.44-.48c1-.27 1.79-1.06 2.06-2.07.5-1.82.5-4.7.5-4.7s0-2.88-.5-4.7ZM9.6 15.5V8.5l6.3 3.5-6.3 3.5Z" />
    </svg>
  );
}

function LinkedInIcon({ size = 16 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="currentColor" focusable="false">
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.45-2.13 2.94v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.11 2.06 2.06 0 0 1 0 4.11Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}

function ClockIcon({ size = 14 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  );
}

function ProductSearchBar({
  t,
  navigate,
  initialValue = "",
  autoFocusSignal = false,
}) {
  const { lang } = useLangContext();
  const [term, setTerm] = useState(initialValue);
  const [lastInitial, setLastInitial] = useState(initialValue);
  if (initialValue !== lastInitial) {
    setLastInitial(initialValue);
    setTerm(initialValue);
  }

  // suggestions.q = termo do último fetch concluído. Loading é derivado:
  // (length>=3) AND (suggestions.q !== termo atual). Evita setState em effect.
  const [suggestions, setSuggestions] = useState({ q: null, list: [] });
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const trimmed = term.trim();
  const isReady = trimmed.length >= SUGGEST_MIN_CHARS;
  const matchesCurrent = suggestions.q === trimmed;
  const list = matchesCurrent ? suggestions.list : [];
  const fetching = isReady && !matchesCurrent;
  const showDropdown = focused && isReady;

  // Fetch debounced só dispara com >=3 chars. Cancela em key-stroke novo.
  useEffect(() => {
    if (!isReady) return undefined;
    let cancelled = false;
    const handle = setTimeout(() => {
      fetch(
        `/api/products?q=${encodeURIComponent(trimmed)}&size=8&lang=${lang}`
      )
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled) return;
          const next = Array.isArray(data?.content) ? data.content : [];
          setSuggestions({ q: trimmed, list: next });
        })
        .catch(() => {
          if (cancelled) return;
          setSuggestions({ q: trimmed, list: [] });
        });
    }, SUGGEST_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [trimmed, isReady, lang]);

  // Fechar dropdown ao clicar fora.
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!autoFocusSignal) return;
    inputRef.current?.focus();
  }, [autoFocusSignal]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setFocused(false);
    navigate(trimmed ? `/catalog?q=${encodeURIComponent(trimmed)}` : "/catalog");
  };

  const handlePick = (product) => {
    const picked =
      product?.name?.[lang] ?? product?.name?.pt ?? product?.ref ?? "";
    setTerm(picked);
    setFocused(false);
    // Vai direto pro detalhe — `tituloDisplay` (ex.: "PEÇAS GUTMANN
    // GUTMANN") é gerado pelo backend a partir de categoria+fabricante e
    // não é searchable, então re-buscar pelo nome devolve 0 resultados.
    const slug = suggestProductSlug(product);
    if (slug) {
      navigate(`/produto/${slug}`);
    } else {
      navigate(`/catalog?q=${encodeURIComponent(picked)}`);
    }
  };

  return (
    <div className="search-bar-wrap" ref={containerRef}>
      <form className="search-bar" onSubmit={handleSubmit} role="search">
        <span className="search-bar-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          className="search-bar-input"
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={t("search_placeholder")}
          aria-label={t("search_aria") || t("search_placeholder")}
          aria-autocomplete="list"
          autoComplete="off"
        />
        {/* Rótulo em <span>: o CSS esconde `span:not(.arr)` no topbar e em
            ≤768. Como nó de texto solto o seletor não pegava e o botão
            carregava 90px de "Buscar" mesmo onde só cabe a seta. */}
        <button type="submit" className="search-bar-submit">
          <span>{t("search_submit")}</span> <span className="arr" aria-hidden="true">→</span>
        </button>
      </form>

      {showDropdown && (
        <div className="search-suggestions" role="listbox">
          {fetching && (
            <div className="search-suggest-empty">{t("loading")}</div>
          )}
          {!fetching && list.length === 0 && (
            <div className="search-suggest-empty">
              {t("search_no_suggest")}
            </div>
          )}
          {!fetching &&
            list.map((p) => {
              const meta = [displayRef(p.ref), p.brand, p.category?.[0]]
                .filter(Boolean)
                .join(" · ");
              const desc = snippetFromDesc(p.desc?.[lang] ?? p.desc?.pt);
              return (
                <button
                  key={p.id ?? p.ref}
                  type="button"
                  className="search-suggest-item"
                  onClick={() => handlePick(p)}
                  role="option"
                  aria-selected="false"
                >
                  <div className="search-suggest-name">
                    {p.name?.[lang] ?? p.name?.pt ?? ""}
                  </div>
                  {meta && <div className="search-suggest-meta">{meta}</div>}
                  {desc && (
                    <div className="search-suggest-desc">{desc}</div>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// Identifica qual variante de mega menu o item dispara. Casamos pelo path PT
// já normalizado (sem locale) — `mapToPtPath` resolve aliases (/produto,
// /catalog, /sales, etc) pros canônicos /pecas, /servicos, /maquinas.
function megaMenuKindForPath(ptPath) {
  if (!ptPath) return null;
  const base = String(ptPath).split("?")[0];
  if (base === "/pecas" || base.startsWith("/pecas/")) return "produtos";
  if (base === "/servicos" || base.startsWith("/servicos/")) return "servicos";
  if (base === "/maquinas" || base.startsWith("/maquinas/")) return "maquinas";
  return null;
}

function neutralPath(path) {
  const base = String(path ?? "/").split("?")[0] || "/";
  return base.replace(/^\/(pt|en|es)(?=\/|$)/, "") || "/";
}

// ----- Link de volta -----
// Cinco páginas de detalhe tinham cinco versões disso: duas com rótulo em
// português cravado no JSX (quebrado em /en e /es), uma dependendo de
// `t("back_to_catalog")` — chave que o CMS não define, então `t()` devolvia
// "" e sobrava só a seta, sem nome acessível nenhum — e cada uma com um
// tamanho e uma cor. Aqui vira um controle só.
//
// Mesmo esquema de fallback do SEE_ALL: a chave do CMS ganha quando existe,
// senão cai no mapa local por idioma. O rótulo nunca fica vazio, que era o
// que transformava o link numa seta solta de 43px.
const BACK_LABELS = {
  catalog: { pt: "Todas as peças", en: "All parts", es: "Todas las piezas" },
  machines: { pt: "Todas as máquinas", en: "All machines", es: "Todas las máquinas" },
  services: { pt: "Todos os serviços", en: "All services", es: "Todos los servicios" },
  jobs: { pt: "Todas as vagas", en: "All openings", es: "Todas las vacantes" },
};

function BackLink({ href, kind, lang, label }) {
  const fallback = BACK_LABELS[kind] ?? BACK_LABELS.catalog;
  const text = label || fallback[lang] || fallback.pt;
  return (
    <Link className="back-link" href={href}>
      <span className="arr-back" aria-hidden="true">←</span>
      {text}
    </Link>
  );
}

// ----- Topbar -----
// Fallback local do rótulo "Ver tudo" — `t()` devolve "" pra chave ausente
// e o blob de strings vem do CMS, que ainda não define `see_all`.
const SEE_ALL = { pt: "Ver tudo em", en: "View all", es: "Ver todo en" };

function Topbar({ route, navigate, lang, setLang, t }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Em mobile o submenu/mega-menu fica colapsado por padrão — `mobileExpanded`
  // guarda o id do nav-item atualmente expandido (ou null). No desktop o hover
  // continua resolvendo a abertura e esse estado fica ignorado pelo CSS.
  const [mobileExpanded, setMobileExpanded] = useState(null);
  // No drawer mobile o tap na linha inteira expande o submenu em vez de
  // navegar — o parent continua alcançável pelo "Ver tudo" dentro dele.
  // Acima de 1100px o menu inline volta e o Link navega normalmente.
  const [isMobileNav, setIsMobileNav] = useState(false);
  // Após click num parent (Peças/Serviços/Máquinas), suprime o mega-menu até
  // o cursor sair do nav-item. Sem isso o dropdown permanece visível depois
  // da navegação porque `:hover` continua aplicando enquanto o cursor está
  // parado em cima do link.
  const [hoverDismissed, setHoverDismissed] = useState(null);
  // Suprime o realce `active` do parent (ex: Produtos) após clicar num item do
  // sub-menu. Sem isso, Produtos continua realçado porque o facet navega pra
  // `/pecas?categoria=x` — mesmo pathname `/pecas` do target do parent. Em
  // Serviços isso não acontece porque o item vai pra `/servicos/slug` (path
  // diferente). Reset ocorre no click do parent ou quando a rota muda.
  const [suppressActiveId, setSuppressActiveId] = useState(null);
  // Qual mega-menu está aberto por hover no desktop. Controlado por JS (não só
  // CSS :hover) porque o mega ancora no fim da .shell (110px) enquanto o link
  // fica centralizado — há uma "zona morta" de ~40px entre o link e o dropdown.
  // Um delay no fechamento mantém o menu aberto enquanto o cursor atravessa
  // esse vão, sem depender da geometria nem de pontes full-width (que faziam
  // o hover de um item disparar o mega de outro).
  const [hoverMega, setHoverMega] = useState(null);
  const megaCloseTimer = useRef(null);
  // Anti-reabertura por evento fantasma. Ao clicar num item do mega, a busca
  // roda e o layout se desloca sob o cursor PARADO — em Produtos isso é
  // assíncrono (fetch dos resultados ~300ms depois), então o browser emite um
  // `mouseenter` FANTASMA no nav-item mesmo sem o usuário mover o mouse,
  // reabrindo o mega. Um hover REAL sempre tem `mousemove` concomitante ao
  // `mouseenter` (o cursor está atravessando a borda); o fantasma vem com o
  // cursor imóvel. Só abrimos o mega se houve movimento real há pouquíssimos ms.
  const lastPointerMove = useRef(0);
  useEffect(() => {
    const onMove = (event) => { lastPointerMove.current = event.timeStamp; };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  const openMegaHover = (id, eventTimeStamp) => {
    if (eventTimeStamp - lastPointerMove.current > 120) return; // enter fantasma
    if (megaCloseTimer.current) {
      clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
    setHoverMega(id);
  };
  const scheduleCloseMegaHover = () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    megaCloseTimer.current = setTimeout(() => setHoverMega(null), 180);
  };
  const { menu, meta, footer } = useSiteData();
  const toMenuItem = (m) => {
    const ptPath = mapToPtPath(m.href || `/${m.slug}` || "/");
    return {
      id: m.id ?? m.href ?? m.slug ?? m.label,
      // `target` é o path real PT pra Link/router (ex: /sobre, /pecas) já
      // prefixado com o locale corrente (`/pt/sobre`, `/en/pecas`, …).
      target: withLocale(ptPath, lang),
      mega: megaMenuKindForPath(ptPath),
      l: m.label,
      children: Array.isArray(m.children) ? m.children.map(toMenuItem) : [],
    };
  };
  const isActive = (item) => {
    const current = neutralPath(route);
    const target = neutralPath(item.target);

    if (current === target || item.children.some((child) => route === child.target)) {
      return true;
    }

    if (item.mega === "produtos") {
      return current === "/pecas" || current.startsWith("/produto/");
    }
    if (item.mega === "servicos") {
      return current === "/servicos" || current.startsWith("/servicos/");
    }
    if (item.mega === "maquinas") {
      return current === "/maquinas" || current.startsWith("/maquinas/");
    }

    return false;
  };

  const items = Array.isArray(menu) ? menu.map(toMenuItem) : [];

  const closeDrawers = () => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMobileExpanded(null);
  };

  // Tira o foco do link clicado. Sem isso, `.has-mega:focus-within` (regra de
  // acessibilidade pra teclado) reabre o mega assim que o `hoverDismissed`
  // expira — obrigando um 2º click fora pra fechar.
  const blurActive = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Fecha o mega-menu e suprime o hover até o usuário mover o mouse de novo.
  const closeMegaMenu = (itemId) => {
    closeDrawers();
    setHoverMega(null);
    blurActive();
    if (megaCloseTimer.current) {
      clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
    if (itemId) {
      setHoverDismissed(itemId);
      setSuppressActiveId(itemId); // tira o realce do parent após o click
    }
  };

  // Trava o scroll da página com o drawer/busca aberto. A trava vai no
  // documentElement, não no body: `html` é `overflow-x: clip`, então o
  // overflow do body não propaga mais pro viewport e seria no-op.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    const sync = () => setIsMobileNav(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;
    const root = document.documentElement;
    const prev = root.style.overflowY;
    root.style.overflowY = "hidden";
    return () => {
      root.style.overflowY = prev;
    };
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, searchOpen]);

  // Fecha drawers de menu/busca quando a rota muda (busca submetida, click
  // em sugestão, link externo). Padrão setState-during-render do React.
  // `hoverDismissed` NÃO é resetado aqui — o click do parent dispara
  // a navegação E o set do dismiss, então resetar na mudança de rota
  // desfaz o efeito imediatamente. O reset acontece só no onMouseLeave
  // do nav-item.
  const [lastRoute, setLastRoute] = useState(route);
  if (route !== lastRoute) {
    setLastRoute(route);
    setMenuOpen(false);
    setSearchOpen(false);
    setMobileExpanded(null);
    // Só libera o realce se saímos do path do item suprimido. Um facet muda a
    // query mantendo o pathname (ex: /pecas), então a supressão deve persistir;
    // navegar pra outra seção (pathname diferente) reativa o realce normal.
    if (suppressActiveId != null) {
      const supItem = items.find((it) => it.id === suppressActiveId);
      if (!supItem || route !== supItem.target) setSuppressActiveId(null);
    }
  }

  // Barra inicial obrigatória: sem ela o fallback resolvia relativo à rota
  // (/pt/pecas/logo.png) e next/image recusa src relativo.
  const logoSrc = meta?.logoUrl || footer?.brandLogoUrl || "/logo.png";
  const logoAlt = meta?.title || footer?.brandTitle || "";

  const topbarClasses = [
    "topbar",
    menuOpen ? "menu-open" : "",
    searchOpen ? "search-open" : "",
  ].filter(Boolean).join(" ");

  // Utility bar: contatos à esquerda (telefone, email geral, email comercial,
  // horário), social à direita. Todos os campos vêm do mesmo `footer` que
  // alimenta o rodapé. A barra inteira só renderiza se ao menos um campo
  // estiver populado.
  const utilPhone = Array.isArray(footer?.contactPhones)
    ? footer.contactPhones.find(Boolean) ?? ""
    : "";
  const utilEmail = footer?.contactEmail ?? "";
  const utilEmailSales = footer?.contactEmailSales ?? "";
  const utilHours = footer?.workingHours ?? "";
  const utilInstagram = footer?.instagramUrl ?? "";
  const utilFacebook = footer?.facebookUrl ?? "";
  const utilYoutube = footer?.youtubeUrl ?? "";
  const utilLinkedin = footer?.linkedinUrl ?? "";
  const phoneTel = utilPhone.replace(/[^+\d]/g, "");
  const showUtil = Boolean(
    utilPhone || utilEmail || utilEmailSales || utilHours ||
    utilInstagram || utilFacebook || utilYoutube || utilLinkedin
  );

  return (
    <header className={topbarClasses}>
      {showUtil && (
        <div className="topbar-util">
          <div className="shell topbar-util-row">
            <div className="topbar-util-contacts">
              {utilPhone && (
                <a className="topbar-util-link" href={`tel:${phoneTel}`} aria-label={t("phone_label") || "Telefone"}>
                  <PhoneIcon size={14} />
                  <span>{utilPhone}</span>
                </a>
              )}
              {utilEmail && (
                <a className="topbar-util-link" href={`mailto:${utilEmail}`} aria-label="Email">
                  <MailIcon size={14} />
                  <span>{utilEmail}</span>
                </a>
              )}
              {utilEmailSales && utilEmailSales !== utilEmail && (
                <a className="topbar-util-link" href={`mailto:${utilEmailSales}`} aria-label="Email comercial">
                  <MailIcon size={14} />
                  <span>{utilEmailSales}</span>
                </a>
              )}
              {utilHours && (
                <span className="topbar-util-link topbar-util-hours" aria-label={t("hours_label") || "Horário"}>
                  <ClockIcon size={14} />
                  <span>{utilHours}</span>
                </span>
              )}
            </div>
            <div className="topbar-util-social" aria-label="Redes sociais">
              {utilInstagram && (
                <a className="topbar-util-soc" href={utilInstagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <InstagramIcon size={16} />
                </a>
              )}
              {utilFacebook && (
                <a className="topbar-util-soc" href={utilFacebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FacebookIcon size={16} />
                </a>
              )}
              {utilYoutube && (
                <a className="topbar-util-soc" href={utilYoutube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <YouTubeIcon size={16} />
                </a>
              )}
              {utilLinkedin && (
                <a className="topbar-util-soc" href={utilLinkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <LinkedInIcon size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="shell row">
        <Link href={`/${lang}`} onClick={closeDrawers} className="brand" aria-label={t("brand_aria")} style={{ display: "flex", alignItems: "center" }}>
          {/* Medido: 162 CSS px ≤1100, 198 acima. Fonte tem 200×100 —
              o otimizador não faz upscale, então width/height = intrínseco. */}
          <Image
            className="topbar-logo"
            src={logoSrc}
            alt={logoAlt}
            width={200}
            height={100}
            loading="eager"
          />
        </Link>
        <div id="topbar-search-panel" className="topbar-search">
          {/* Fecha o drawer no submit/sugestão. O efeito de rota só olha o
              pathname, e buscar de dentro do catálogo troca só a query — o
              drawer ficava por cima do resultado que acabou de carregar. */}
          <ProductSearchBar
            key={searchOpen ? "search-open" : "search-closed"}
            t={t}
            navigate={(path) => {
              setSearchOpen(false);
              navigate(path);
            }}
            autoFocusSignal={searchOpen}
          />
          <button
            type="button"
            className="search-close"
            aria-label={t("search_close") || "Fechar busca"}
            onClick={() => setSearchOpen(false)}
          >
            <span />
            <span />
          </button>
        </div>
        <button
          className="search-toggle"
          type="button"
          aria-label={t("search_toggle_aria")}
          aria-expanded={searchOpen}
          aria-controls="topbar-search-panel"
          onClick={() => {
            setSearchOpen((open) => !open);
            setMenuOpen(false);
          }}
        >
          <SearchIcon size={20} />
        </button>
        <button
          className="menu-toggle"
          type="button"
          aria-label={menuOpen ? t("menu_close") : t("menu_open")}
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          onClick={() => {
            setMenuOpen((open) => !open);
            setSearchOpen(false);
          }}
        >
          <span />
          <span />
          <span />
        </button>
        <nav id="site-menu" className="site-menu">
          {items.map((it) => {
            const hasPopup = it.mega != null || it.children.length > 0;
            const isMobileOpen = mobileExpanded === it.id;
            const isHoverDismissed = hoverDismissed === it.id;
            const isMegaHover = it.mega != null && hoverMega === it.id;
            return (
              <div
                key={it.id}
                className={
                  "nav-item " +
                  (hasPopup ? "has-submenu " : "") +
                  (it.mega ? `has-mega has-mega-${it.mega} ` : "") +
                  (isActive(it) && suppressActiveId !== it.id ? "active " : "") +
                  (isMobileOpen ? "mobile-open " : "") +
                  (isMegaHover ? "mega-hover " : "") +
                  (isHoverDismissed ? "hover-dismissed" : "")
                }
                onMouseEnter={(event) => {
                  // NÃO limpar `hoverDismissed` aqui. Após clicar num item do
                  // mega (parent ou facet), a navegação dispara a busca e o
                  // layout desloca sob o cursor parado — o browser re-emite um
                  // `mouseenter` espúrio. Limpar o dismiss aqui reabriria o
                  // mega logo após o click. O dismiss só cai no mouseleave real
                  // (abaixo). `.hover-dismissed` usa `!important`, então o
                  // openMegaHover abaixo fica suprimido até o cursor sair.
                  if (it.mega != null) openMegaHover(it.id, event.timeStamp);
                }}
                onMouseLeave={() => {
                  if (it.mega != null) scheduleCloseMegaHover();
                  if (isHoverDismissed) {
                    // Reseta após um pequeno delay para suavizar a transição
                    setTimeout(() => setHoverDismissed(null), 150);
                  }
                }}
              >
                <Link
                  href={it.target}
                  onClick={(e) => {
                    if (isMobileNav && hasPopup) {
                      e.preventDefault();
                      setMobileExpanded((cur) => (cur === it.id ? null : it.id));
                      return;
                    }
                    closeDrawers();
                    if (hasPopup) {
                      setHoverMega(null);
                      setHoverDismissed(it.id);
                      setSuppressActiveId(null); // parent clicado → realça
                      blurActive();
                    }
                  }}
                  className="nav-link"
                  aria-haspopup={hasPopup ? "true" : undefined}
                  aria-expanded={isMobileNav && hasPopup ? isMobileOpen : undefined}
                >
                  {it.l}
                </Link>
                {hasPopup && (
                  <button
                    type="button"
                    className="nav-expand"
                    aria-label={isMobileOpen ? t("submenu_close") || "Recolher" : t("submenu_open") || "Expandir"}
                    aria-expanded={isMobileOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMobileExpanded((cur) => (cur === it.id ? null : it.id));
                    }}
                  >
                    <span className="chev" aria-hidden="true" />
                  </button>
                )}
                {hasPopup && (
                  <Link
                    href={it.target}
                    className="nav-see-all"
                    onClick={closeDrawers}
                  >
                    {t("see_all") || SEE_ALL[lang] || SEE_ALL.pt} {it.l}{" "}
                    <span className="arr" aria-hidden="true">→</span>
                  </Link>
                )}
                {it.mega ? (
                  <MegaMenu
                    kind={it.mega}
                    label={it.l}
                    itemId={it.id}
                    lang={lang}
                    t={t}
                    onNavigate={() => closeMegaMenu(it.id)}
                  />
                ) : (
                  it.children.length > 0 && (
                    <div className="submenu" aria-label={`${t("submenu_aria")} ${it.l}`}>
                      {it.children.map((child) => (
                        <Link
                          key={child.id}
                          href={child.target}
                          onClick={closeDrawers}
                          className={route === child.target ? "active" : ""}
                        >
                          {child.l}
                        </Link>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })}
          <div className="mobile-menu-tools">
            <div className="menu-label">{t("language_label")}</div>
            <div className="lang-switch mobile-lang" role="group" aria-label={t("language_label")}>
              {["pt", "en", "es"].map((l) => (
                <button key={l} className={lang === l ? "on" : ""} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
            <Link className="cta-btn" href={withLocale("/contato", lang)} onClick={closeDrawers}>
              {t("cta_short")} <span className="arr" aria-hidden="true">→</span>
            </Link>
          </div>
        </nav>
        <div className="right">
          <div className="lang-switch desktop-lang" role="group" aria-label={t("language_label")}>
            {["pt", "en", "es"].map((l) => (
              <button key={l} className={lang === l ? "on" : ""} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
            ))}
          </div>
          <Link className="cta-btn" href={withLocale("/contato", lang)} onClick={closeDrawers}>
            {t("cta_short")} <span className="arr" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

// ----- MegaMenu -----
// Painel largo (estilo Nike) que substitui o submenu simples nos itens
// Produtos / Serviços / Comércio de Máquinas. Os dados vêm de useSiteData()
// — facets do ERP via /site/categorias|fabricantes|marcas|series e listas
// /site/services + /site/machines.

const MEGA_COLUMN_LIMIT = 10;

function pickName(item, lang) {
  if (!item) return "";
  // CMS pode entregar `nome` escalar (resolvido por ?lang=) ou bag i18n
  // `nomeI18n: {pt, en, es}`. Normalizamos pro idioma corrente, com fallback.
  const bag = item.nomeI18n ?? item.labelI18n ?? null;
  if (bag && typeof bag === "object") {
    return bag[lang] ?? bag.pt ?? bag.en ?? bag.es ?? "";
  }
  return item.nome ?? item.label ?? item.name ?? "";
}

function MegaMenuFacetColumn({ title, items, lang, hrefFor, onNavigate }) {
  const visible = items.slice(0, MEGA_COLUMN_LIMIT);
  const overflow = Math.max(0, items.length - visible.length);
  return (
    <div className="mega-col">
      <h4 className="mega-col-title">{title}</h4>
      <ul className="mega-col-list">
        {visible.map((it) => {
          const label = pickName(it, lang);
          if (!label) return null;
          return (
            <li key={it.id ?? it.slug ?? label}>
              <Link href={hrefFor(it)} onClick={onNavigate}>
                {label}
              </Link>
            </li>
          );
        })}
        {overflow > 0 && (
          <li className="mega-col-more">+ {overflow}</li>
        )}
      </ul>
    </div>
  );
}

function MegaMenuCategoryColumn({ title, items, lang, hrefFor, hrefForChild, hrefForGrandchild, onNavigate }) {
  const visible = items.slice(0, MEGA_COLUMN_LIMIT);
  const overflow = Math.max(0, items.length - visible.length);
  
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredTipo, setHoveredTipo] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTipoId, setExpandedTipoId] = useState(null);

  // Intencao de hover. A linha inteira e area de sensivel e a coluna tem 7
  // delas: arrastar o mouse de cima a baixo disparava os 7 paineis em
  // sequencia. Com o atraso, so abre onde o cursor de facto para. Teclado
  // nao espera — foco e intencao explicita.
  const hoverTimer = useRef(null);
  const openCategory = (id) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoveredCategory(id);
      setHoveredTipo(null);
    }, 130);
  };
  const cancelPendingHover = () => clearTimeout(hoverTimer.current);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  return (
    <div
      className="mega-col mega-col-categories"
      onMouseLeave={() => { cancelPendingHover(); setHoveredCategory(null); setHoveredTipo(null); }}
      // Sem isto o flyout so abria no mouseenter e os niveis tipo/modelo
      // eram inalcancaveis por teclado no desktop (o acordeao alternativo e
      // display:none acima de 1100px). Fecha so quando o foco sai da coluna
      // inteira — relatedTarget e pra onde o foco esta indo.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setHoveredCategory(null);
          setHoveredTipo(null);
        }
      }}
    >
      <h4 className="mega-col-title">{title}</h4>
      <ul className="mega-col-list">
        {visible.map((it) => {
          const label = pickName(it, lang);
          if (!label) return null;
          const hasChildren = it.children && it.children.length > 0;
          
          return (
            <li 
              key={it.id ?? it.slug ?? label}
              onMouseEnter={() => openCategory(it.id)}
              onFocus={() => { cancelPendingHover(); setHoveredCategory(it.id); setHoveredTipo(null); }}
              className={hasChildren ? "has-flyout" : ""}
            >
              <div className="category-item-wrap">
                <Link href={hrefFor(it)} onClick={onNavigate}>
                  {label}
                </Link>
                {hasChildren && (
                  <button type="button"
                    className="mobile-toggle nav-expand"
                    aria-expanded={expandedId === it.id}
                    onClick={(e) => {
                      e.preventDefault();
                      setExpandedId(expandedId === it.id ? null : it.id);
                    }}
                  >
                    <span className="chev"></span>
                  </button>
                )}
              </div>
              
              {hasChildren && hoveredCategory === it.id && (
                <div className="category-flyout desktop-only">
                  <ul className="mega-col-list">
                    {it.children.map(child => {
                      const childLabel = pickName(child, lang);
                      const hasGrandchildren = child.children && child.children.length > 0;
                      return (
                        <li 
                          key={child.id}
                          onMouseEnter={() => setHoveredTipo(child.id)}
                          onFocus={(e) => { e.stopPropagation(); setHoveredTipo(child.id); }}
                          className={hasGrandchildren ? "has-flyout" : ""}
                        >
                          <div className="category-item-wrap">
                            <Link href={hrefForChild(child)} onClick={onNavigate}>
                              {childLabel}
                            </Link>
                            {hasGrandchildren && <span className="chev-right" aria-hidden="true" />}
                          </div>

                          {hasGrandchildren && hoveredTipo === child.id && (
                            <div className="category-flyout desktop-only">
                              <ul className="mega-col-list">
                                {child.children.map(grandchild => (
                                  <li key={grandchild.id}>
                                    <Link href={hrefForGrandchild(grandchild)} onClick={onNavigate}>
                                      {pickName(grandchild, lang)}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {hasChildren && expandedId === it.id && (
                <div className="category-accordion mobile-only">
                  <ul className="mega-col-list">
                    {it.children.map(child => {
                      const hasGrandchildren = child.children && child.children.length > 0;
                      return (
                        <li key={child.id}>
                          <div className="category-item-wrap">
                            <Link href={hrefForChild(child)} onClick={onNavigate}>
                              {pickName(child, lang)}
                            </Link>
                            {hasGrandchildren && (
                              <button type="button"
                                className="mobile-toggle nav-expand"
                                aria-expanded={expandedTipoId === child.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setExpandedTipoId(expandedTipoId === child.id ? null : child.id);
                                }}
                                          >
                                <span className="chev"></span>
                              </button>
                            )}
                          </div>
                          
                          {hasGrandchildren && expandedTipoId === child.id && (
                            <div className="category-accordion mobile-only">
                              <ul className="mega-col-list">
                                {child.children.map(grandchild => (
                                  <li key={grandchild.id}>
                                    <Link href={hrefForGrandchild(grandchild)} onClick={onNavigate}>
                                      {pickName(grandchild, lang)}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
        {overflow > 0 && (
          <li className="mega-col-more">+ {overflow}</li>
        )}
      </ul>
    </div>
  );
}

function MegaMenuProdutos({ lang, t, onNavigate }) {
  const { categorias, marcas, series } = useSiteData();
  const cats = Array.isArray(categorias) ? categorias : [];
  const mks = Array.isArray(marcas) ? marcas : [];
  const srs = Array.isArray(series) ? series : [];

  const base = withLocale("/pecas", lang);
  // CatalogPage lê os filtros pelos nomes curtos (`categoria`, `marca`,
  // `serie`). Internamente ela traduz pra `marcaSlug=` etc. ao chamar a API.
  // Aqui usamos a forma curta pra bater com o que a página espera.
  const linkWithParam = (key, slug) =>
    `${base}?${key}=${encodeURIComponent(slug)}`;

  return (
    <div className="mega-grid mega-grid-produtos">
      <MegaMenuCategoryColumn
        title={t("mega_col_category") || "Categoria"}
        items={cats}
        lang={lang}
        hrefFor={(c) => linkWithParam("categoria", c.slug ?? c.id)}
        hrefForChild={(c) => linkWithParam("tipo", c.slug ?? c.id)}
        hrefForGrandchild={(c) => linkWithParam("modelo", c.slug ?? c.id)}
        onNavigate={onNavigate}
      />
      <MegaMenuFacetColumn
        title={t("mega_col_brand") || "Marca"}
        items={mks}
        lang={lang}
        hrefFor={(m) => linkWithParam("marca", m.slug ?? m.id)}
        onNavigate={onNavigate}
      />
      <MegaMenuFacetColumn
        title={t("mega_col_series") || "Série"}
        items={srs}
        lang={lang}
        hrefFor={(s) => linkWithParam("serie", s.slug ?? s.id)}
        onNavigate={onNavigate}
      />
    </div>
  );
}

function MegaMenuServicos({ lang, t, onNavigate }) {
  const { services } = useSiteData();
  const list = Array.isArray(services) ? services : [];
  const total = list.length;
  return (
    <div className="mega-grid mega-grid-servicos">
      {list.map((s, i) => {
        const slug = s.slug ?? null;
        const href = slug
          ? withLocale(`/servicos/${slug}`, lang)
          : withLocale("/servicos", lang);
        const name = s.name?.[lang] ?? s.name?.pt ?? "";
        const desc = s.desc?.[lang] ?? s.desc?.pt ?? "";
        return (
          <Link
            key={s.id ?? s.code ?? i}
            href={href}
            onClick={onNavigate}
            className="mega-service-card"
          >
            <span className="mega-service-code">{s.code}</span>
            <span className="mega-service-name">{name}</span>
            {desc && <span className="mega-service-desc">{desc}</span>}
            <span className="mega-service-cta">
              {s.ctaLabel || t("mega_quote_cta") || "Solicitar orçamento"}
              <span className="arr" aria-hidden="true">→</span>
            </span>
            <span className="mega-service-index">
              {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

const MACHINE_STATUS_OPTIONS = [
  { value: "available", labelKey: "sales_status_available" },
  { value: "reserved", labelKey: "sales_status_reserved" },
  { value: "new", labelKey: "sales_status_new" },
  { value: "sold", labelKey: "sales_status_sold" },
];

function MegaMenuMaquinas({ lang, t, onNavigate }) {
  const { machines } = useSiteData();
  const list = Array.isArray(machines) ? machines : [];
  const base = withLocale("/maquinas", lang);
  const types = Array.from(new Set(list.map((m) => m.type).filter(Boolean)));

  return (
    <div className="mega-grid mega-grid-maquinas">
      <div className="mega-col">
        <h4 className="mega-col-title">{t("sales_filter_status") || "Status"}</h4>
        <ul className="mega-col-list">
          <li>
            <Link href={base} onClick={onNavigate}>
              {t("all") || "Todos"}
            </Link>
          </li>
          {MACHINE_STATUS_OPTIONS.map((opt) => (
            <li key={opt.value}>
              <Link
                href={`${base}?status=${encodeURIComponent(opt.value)}`}
                onClick={onNavigate}
              >
                {t(opt.labelKey) || opt.value}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="mega-col">
        <h4 className="mega-col-title">{t("sales_filter_type") || "Tipo"}</h4>
        <ul className="mega-col-list">
          <li>
            <Link href={base} onClick={onNavigate}>
              {t("all") || "Todos"}
            </Link>
          </li>
          {types.map((tp) => (
            <li key={tp}>
              <Link
                href={`${base}?tipo=${encodeURIComponent(tp)}`}
                onClick={onNavigate}
              >
                {tp}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MegaMenu({ kind, label, lang, t, onNavigate }) {
  return (
    <div
      className={`submenu mega-menu mega-${kind}`}
      role="region"
      aria-label={label}
    >
      {kind === "produtos" && (
        <MegaMenuProdutos lang={lang} t={t} onNavigate={onNavigate} />
      )}
      {kind === "servicos" && (
        <MegaMenuServicos lang={lang} t={t} onNavigate={onNavigate} />
      )}
      {kind === "maquinas" && (
        <MegaMenuMaquinas lang={lang} t={t} onNavigate={onNavigate} />
      )}
    </div>
  );
}

function WhatsAppIcon({ size = 20 }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      focusable="false"
    >
      <path d="M16.03 3.2c-7.05 0-12.78 5.65-12.78 12.6 0 2.22.6 4.39 1.73 6.28L3.14 28.8l6.96-1.79a12.96 12.96 0 0 0 5.93 1.49c7.05 0 12.78-5.65 12.78-12.6S23.08 3.2 16.03 3.2Zm0 22.98c-1.9 0-3.75-.5-5.37-1.45l-.39-.23-4.13 1.06 1.1-3.9-.26-.4a10.2 10.2 0 0 1-1.56-5.46c0-5.66 4.77-10.27 10.61-10.27s10.61 4.61 10.61 10.27-4.77 10.38-10.61 10.38Zm5.82-7.68c-.32-.16-1.9-.92-2.19-1.03-.3-.1-.51-.16-.73.16-.21.31-.83 1.03-1.02 1.24-.19.21-.37.24-.69.08-.32-.16-1.35-.49-2.58-1.55-.95-.84-1.59-1.87-1.78-2.19-.19-.31-.02-.48.14-.64.15-.15.32-.37.48-.55.16-.18.21-.31.32-.52.11-.21.05-.39-.03-.55-.08-.16-.73-1.73-1-2.37-.26-.62-.53-.54-.73-.55h-.62c-.21 0-.56.08-.86.39-.3.31-1.13 1.09-1.13 2.66s1.16 3.09 1.32 3.3c.16.21 2.29 3.45 5.55 4.83.78.33 1.38.53 1.85.68.78.24 1.48.21 2.04.13.62-.09 1.9-.76 2.17-1.5.27-.73.27-1.36.19-1.5-.08-.13-.29-.21-.61-.37Z" />
    </svg>
  );
}

// Formata o numeroE164 do backend ("5511976289954") pro padrão visual
// "+55 11 97628-9954". Se não bater com o regex (formato exotico), devolve
// com prefixo `+`.
function formatPhoneE164(e164) {
  const digits = String(e164 ?? "").replace(/\D/g, "");
  if (!digits) return "";
  const m = digits.match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `+${m[1]} ${m[2]} ${m[3]}-${m[4]}` : `+${digits}`;
}

function WhatsAppLink({ className = "", children, ariaLabel }) {
  const { whatsapp } = useSiteData();
  if (!whatsapp?.linkWhatsApp) return null;
  // Se children não vier, mostra o número formatado como rótulo padrão.
  const label = children ?? formatPhoneE164(whatsapp.numeroE164);
  // Quando há conteúdo visível, ele vira o nome acessível (atende WCAG 2.5.3
  // "Label in Name"). aria-label fica apenas como fallback quando nem children
  // nem o número formatado estão disponíveis.
  const a11yLabel = label ? undefined : (ariaLabel || whatsapp.ariaLabel || undefined);
  return (
    <a
      className={className}
      href={whatsapp.linkWhatsApp}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={a11yLabel}
    >
      <WhatsAppIcon />
      <span>{label}</span>
    </a>
  );
}

function FloatingWhatsApp() {
  const { whatsapp } = useSiteData();
  if (!whatsapp?.linkWhatsApp) return null;
  return (
    <WhatsAppLink className="whatsapp-float" ariaLabel={whatsapp.ariaLabel}>
      <span className="whatsapp-float-label">{whatsapp.ariaLabel ?? ""}</span>
    </WhatsAppLink>
  );
}

// ----- Footer -----
// Hrefs default quando o admin não preenche um link específico nos itens de
// partsLinks/servicesLinks. Backend devolve null quando vazio.
const PARTS_LINK_FALLBACK = "/catalog";
const SERVICES_LINK_FALLBACK = "/services";

// Resolve um valor que pode ser string única (Fase 1) ou objeto i18n bag
// (Fase 2: {pt, en, es}). Sempre cai pra PT se a língua atual não existir.
function pickLang(value, lang) {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("pt" in value || "en" in value || "es" in value)
  ) {
    return value[lang] || value.pt || value.en || value.es || "";
  }
  return value ?? "";
}

function Footer({ t, lang, navigate }) {
  const { footer, meta, whatsapp } = useSiteData();

  const addr = {
    line1: footer?.addressLine1 ?? "",
    line2: footer?.addressLine2 ?? "",
    city: footer?.addressCity ?? "",
    state: footer?.addressState ?? "",
    zip: footer?.addressZip ?? "",
  };

  const phone = footer?.contactPhones?.[0] ?? "";
  const emailMain = footer?.contactEmail ?? "";
  const emailSales = footer?.contactEmailSales ?? "";
  const copyright = footer?.copyrightHolder ?? "";
  const revision = footer?.footerRevision ?? "";

  const partsLinks = Array.isArray(footer?.partsLinks) ? footer.partsLinks : [];
  const servicesLinks = Array.isArray(footer?.servicesLinks) ? footer.servicesLinks : [];

  const logoSrc = footer?.brandLogoUrl || meta?.logoUrl || "/logo.png";
  const logoAlt = footer?.brandTitle || meta?.title || "";

  // Endereço em duas linhas conforme pedido do cliente:
  //   "Av. Patos, 1305 - Cidade Industrial Satélite de São Paulo"
  //   "Guarulhos - SP, 07222-010"
  const addrLine1Display = [addr.line1, addr.line2].filter(Boolean).join(" - ");
  const addrLine2Display = [
    [addr.city, addr.state].filter(Boolean).join(" - "),
    addr.zip,
  ].filter(Boolean).join(", ");

  const renderFooterLink = (item, fallback, key) => {
    // Backend manda label_i18n (snake_case) nesse endpoint, diferente dos
    // demais que usam camelCase. Aceita ambos por segurança.
    const label = pickLang(
      item?.labelI18n ?? item?.label_i18n ?? item?.label,
      lang
    );
    const raw = String(item?.href ?? "").trim();
    if (raw && /^https?:\/\//i.test(raw)) {
      return (
        <li key={key}>
          <a href={raw} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        </li>
      );
    }
    const stripped = raw.replace(/^#/, "");
    const target = stripped.startsWith("/")
      ? stripped
      : stripped
      ? `/${stripped}`
      : fallback;
    return (
      <li key={key}>
        <a
          href={withLocale(mapToPtPath(target), lang)}
          onClick={(e) => {
            e.preventDefault();
            navigate(target);
          }}
        >
          {label}
        </a>
      </li>
    );
  };

  const prideText = pickLang(footer?.prideTextI18n ?? footer?.prideText, lang);
  const showPride = prideText || footer?.prideFlagUrl;

  return (
    <footer>
      <div className="shell">
        <div className="grid">
          <div>
            {/* Medido: 280 CSS px ≤1100, 322 acima — CSS já faz upscale de
                uma fonte 200×100; nada a ganhar pedindo mais que o intrínseco. */}
            <Image
              className="footer-logo"
              src={logoSrc}
              alt={logoAlt}
              width={200}
              height={100}
            />
            <p className="footer-lead">{t("hero_sub")}</p>
            <div className="footer-cta-wrap">
              <Link className="cta-btn cta-btn-on-dark" href={withLocale("/contato", lang)}>
                {t("request_quote")} <span className="arr" aria-hidden="true">→</span>
              </Link>
            </div>
            {showPride && (
              <div className="pride-badge">
                {footer.prideFlagUrl && (
                  // Medido: 22×16.5 CSS px. width/height só fixam a razão
                  // (4:3, igual à fonte 685×514) — o CSS manda no tamanho.
                  // O 30×20 anterior dava 3:2 e achataria pra 14.7px.
                  <Image
                    src={footer.prideFlagUrl}
                    alt=""
                    width={44}
                    height={33}
                  />
                )}
                {prideText && <span>{prideText}</span>}
              </div>
            )}
          </div>
          {partsLinks.length > 0 && (
            <div>
              {t("nav.catalog") && <h4>{t("nav.catalog")}</h4>}
              <ul>
                {partsLinks.map((item, i) =>
                  renderFooterLink(item, PARTS_LINK_FALLBACK, `parts-${i}`)
                )}
              </ul>
            </div>
          )}
          {servicesLinks.length > 0 && (
            <div>
              {t("nav.services") && <h4>{t("nav.services")}</h4>}
              <ul>
                {servicesLinks.map((item, i) =>
                  renderFooterLink(item, SERVICES_LINK_FALLBACK, `svc-${i}`)
                )}
              </ul>
            </div>
          )}
          <div>
            {t("legend_address") && <h4>{t("legend_address")}</h4>}
            <address style={{ margin: 0, fontSize: 14, color: "rgba(244,242,238,0.85)", fontStyle: "normal" }}>
              {addrLine1Display && <p style={{ margin: 0 }}>{addrLine1Display}</p>}
              {addrLine2Display && <p style={{ margin: 0 }}>{addrLine2Display}</p>}
            </address>
            {t("nav.contact") && <h4 style={{ marginTop: 24 }}>{t("nav.contact")}</h4>}
            <ul style={{ fontSize: 14 }}>
              {phone && <li>{phone}</li>}
              {whatsapp?.linkWhatsApp && (
                <li>
                  <WhatsAppLink className="whatsapp-text-link">
                    {formatPhoneE164(whatsapp.numeroE164)}
                  </WhatsAppLink>
                </li>
              )}
              {emailMain && (
                <li><a href={`mailto:${emailMain}`} className="link">{emailMain}</a></li>
              )}
              {emailSales && (
                <li><a href={`mailto:${emailSales}`} className="link">{emailSales}</a></li>
              )}
            </ul>
          </div>
        </div>
        <div className="legal">
          <span>{copyright}</span>
          {revision && <span>{revision}</span>}
          <span className="legal-links">
            <Link href={withLocale("/trabalhe-conosco", lang)}>
              {t("nav.careers") || "Trabalhe conosco"}
            </Link>
            <Link href={withLocale("/politica-de-privacidade", lang)}>
              {t("nav.privacy") || "Privacidade"}
            </Link>
            <Link href={withLocale("/politica-de-cookies", lang)}>
              {t("nav.cookies") || "Cookies"}
            </Link>
            <Link href={withLocale("/termos-de-uso", lang)}>
              {t("nav.terms") || "Termos de uso"}
            </Link>
          </span>
          {footer?.partnerName && (
            <span className="legal-partner">
              {footer?.partnerLabel ? `${footer.partnerLabel} ` : ""}
              {footer?.partnerUrl ? (
                <a href={footer.partnerUrl} target="_blank" rel="noopener noreferrer">
                  {footer.partnerName}
                </a>
              ) : (
                footer.partnerName
              )}
            </span>
          )}
          {footer?.developerName && (
            <span className="legal-credit">
              {footer?.developerCreditLabel ? `${footer.developerCreditLabel} ` : ""}
              {footer?.developerUrl ? (
                <a href={footer.developerUrl} target="_blank" rel="noopener noreferrer">
                  {footer.developerName}
                </a>
              ) : (
                footer.developerName
              )}
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}

// ----- Segment visual -----
// Render do ícone do segmento: prioriza `imagemUrl` (foto enviada pelo admin
// no CRM via POST /crm/site/segments/{id}/imagem); senão cai pra SVG inline
// mapeado por `icone` (string curta tipo "car", "factory", "flame", "tractor").
function segmentSvgBody(icone) {
  switch (icone) {
    case "car":
      return (
        <>
          <circle cx="28" cy="28" r="20" />
          <circle cx="28" cy="28" r="6" />
          <line x1="8" y1="28" x2="48" y2="28" strokeDasharray="2,2" />
        </>
      );
    case "factory":
      return (
        <>
          <rect x="8" y="20" width="40" height="20" />
          <line x1="8" y1="14" x2="48" y2="14" />
          <line x1="8" y1="46" x2="48" y2="46" />
        </>
      );
    case "flame":
      return (
        <>
          <path d="M14 40 L28 12 L42 40 Z" />
          <line x1="14" y1="40" x2="42" y2="40" />
        </>
      );
    case "tractor":
      return (
        <>
          <circle cx="28" cy="28" r="18" />
          <path d="M16 28 L28 18 L40 28 L28 38 Z" />
        </>
      );
    case "hammer":
      return (
        <>
          <rect x="22" y="10" width="12" height="16" />
          <line x1="28" y1="26" x2="28" y2="46" />
        </>
      );
    default:
      // Genérico: anel concêntrico (segmento sem ícone definido)
      return (
        <>
          <circle cx="28" cy="28" r="20" />
          <circle cx="28" cy="28" r="6" />
        </>
      );
  }
}

function SegmentVisual({ segment, size = 112, alt = "" }) {
  // Tamanho via custom property pra que o CSS possa capar no mobile
  // (inline width/height ganharia da media query).
  const sizeVar = { "--seg-ico-size": `${size}px` };
  if (segment?.imagemUrl) {
    return (
      // O slot NÃO é fixo: o CSS capa o ícone em 84–88 CSS px no mobile
      // (globals.css .seg .ico) enquanto `size` é o valor de desktop (112 na
      // home, 160 em /atuacao). Só width/height faria o srcset x-descriptor
      // sair do tamanho de desktop e servir 384 pra um slot de 84 — medido
      // 2,29x. Com `sizes` o browser escolhe 192 nos dois breakpoints.
      <Image
        className="ico ico-photo"
        src={segment.imagemUrl}
        alt={alt}
        width={size}
        height={size}
        sizes={`(max-width: 768px) 96px, ${size}px`}
        style={sizeVar}
      />
    );
  }
  return (
    <svg
      className="ico"
      viewBox="0 0 56 56"
      fill="none"
      stroke="#1A1D21"
      strokeWidth="1.2"
      style={sizeVar}
    >
      {segmentSvgBody(segment?.icone)}
    </svg>
  );
}

// ----- Product media -----
// Mostra a foto real do produto (imagemPrincipalUrl) com a tag/legenda no
// estilo do PlaceholderImage. Se não houver foto cadastrada, cai no
// PlaceholderImage SVG (mantém o look técnico do site).
function ProductMedia({ product, kind = "stripes", priority = false }) {
  if (product?.imagemPrincipalUrl) {
    const alt = product?.cap || product?.name?.pt || product?.ref || "";
    return (
      <div className="img img-with-photo">
        {/* Medido em /pt/pecas: 325 CSS px @375 (1 col), 399 @880 (2 col),
            308 @1024 (3 col), 450 @1440+ (shell trava em 1440). */}
        <Image
          className="img-photo"
          src={product.imagemPrincipalUrl}
          alt={alt}
          fill
          sizes="(max-width: 600px) 88vw, (max-width: 980px) 46vw, (max-width: 1440px) 32vw, 460px"
          priority={priority}
        />
      </div>
    );
  }
  return <PlaceholderImage kind={kind} />;
}

// ----- Image placeholder -----
function PlaceholderImage({ kind = "stripes" }) {
  return (
    <div className="img">
      <div className="img-stripes" />
      <svg width="100%" height="100%" viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet"
           style={{ position: "absolute", inset: 0, opacity: 0.35 }}>
        <g stroke="#2A2E33" strokeWidth="0.5" fill="none">
          {kind === "shaft" && (
            <>
              <rect x="40" y="60" width="120" height="30" />
              <circle cx="60" cy="75" r="8" />
              <circle cx="140" cy="75" r="8" />
              <line x1="40" y1="50" x2="160" y2="50" strokeDasharray="2,2" />
            </>
          )}
          {kind === "gear" && (
            <>
              <circle cx="100" cy="75" r="40" />
              <circle cx="100" cy="75" r="14" />
              <line x1="60" y1="75" x2="140" y2="75" strokeDasharray="2,2" />
              <line x1="100" y1="35" x2="100" y2="115" strokeDasharray="2,2" />
            </>
          )}
          {kind === "rod" && (
            <>
              <circle cx="60" cy="75" r="14" />
              <circle cx="140" cy="75" r="20" />
              <path d="M60,67 L140,55 M60,83 L140,95" />
            </>
          )}
          {kind === "stripes" && (
            <>
              <rect x="50" y="40" width="100" height="70" />
              <line x1="50" y1="55" x2="150" y2="55" />
              <line x1="50" y1="75" x2="150" y2="75" />
              <line x1="50" y1="95" x2="150" y2="95" />
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
export { BackLink, FloatingWhatsApp, Topbar, Footer, PlaceholderImage, ProductMedia, ProductSearchBar, SegmentVisual, WhatsAppIcon, WhatsAppLink, formatPhoneE164 };
